// =============================================================================
// Customers Page - Anagrafica Clienti
// =============================================================================

import { useState } from 'react';
import {
  Users,
  Plus,
  Search,
  Edit2,
  Trash2,
  Loader2,
  AlertCircle,
  Mail,
  Phone,
  MapPin,
  CalendarClock,
} from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { useCustomers, useDeleteCustomer } from '../api/hooks';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '../store/authStore';
import { useSchedulerActions } from '../store/schedulerStore';
import { useToast } from '../store/toastStore';
import { Customer } from '../types';
import { CustomerModal } from '../components/customers/CustomerModal';
import { CustomerAppointmentsModal } from '../components/customers/CustomerAppointmentsModal';
import { EmptyState } from '../components/common';
import { useDebounce } from '../hooks/useDebounce';
import { useDateLocale } from '../hooks/useDateLocale';
import { cn } from '../lib/utils';

// ===========================================================================
// Avatar Component
// ===========================================================================

function CustomerAvatar({ customer }: { customer: Customer }) {
  const initials = `${customer.firstName.charAt(0)}${customer.lastName.charAt(0)}`.toUpperCase();
  
  // Generate color from name (consistent for same name)
  const colors = [
    'bg-blue-500', 'bg-emerald-500', 'bg-purple-500', 'bg-amber-500',
    'bg-rose-500', 'bg-cyan-500', 'bg-indigo-500', 'bg-pink-500',
  ];
  const colorIndex = (customer.firstName.charCodeAt(0) + customer.lastName.charCodeAt(0)) % colors.length;
  
  return (
    <div className={cn(
      'w-10 h-10 rounded-full flex items-center justify-center text-white font-semibold text-sm',
      colors[colorIndex]
    )}>
      {initials}
    </div>
  );
}

// ===========================================================================
// Main Component
// ===========================================================================

export function CustomersPage() {
  const { t } = useTranslation('customers');
  const { user } = useAuthStore();
  const toast = useToast();
  const dateLocale = useDateLocale();
  const { openEditModal } = useSchedulerActions();
  const [searchTerm, setSearchTerm] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState<Customer | null>(null);
  const [historyCustomer, setHistoryCustomer] = useState<Customer | null>(null);

  const debouncedSearch = useDebounce(searchTerm, 300);

  const { data: customersResponse, isLoading, error } = useCustomers(debouncedSearch || undefined);
  const deleteCustomer = useDeleteCustomer();

  const customers = customersResponse?.data ?? [];

  // Dal dettaglio appuntamento si passa alla modifica: chiudo lo storico per
  // non impilare due modali.
  const handleSelectAppointment = (appointmentId: string) => {
    setHistoryCustomer(null);
    openEditModal(appointmentId);
  };

  const handleCreate = () => {
    setEditingCustomer(null);
    setIsModalOpen(true);
  };

  const handleEdit = (customer: Customer) => {
    setEditingCustomer(customer);
    setIsModalOpen(true);
  };

  const handleDelete = async (customer: Customer) => {
    if (window.confirm(t('deleteConfirm', { name: customer.fullName }))) {
      try {
        await deleteCustomer.mutateAsync(customer.id);
      } catch (err) {
        toast.error(t('deleteError'));
      }
    }
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setEditingCustomer(null);
  };

  return (
    <div className="h-full flex flex-col bg-gray-50">
      {/* Page Header */}
      <header className="bg-white border-b border-gray-200 px-6 py-4 flex-shrink-0">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-emerald-50 rounded-lg">
              <Users className="w-5 h-5 text-emerald-600" />
            </div>
            <h1 className="text-lg font-semibold text-gray-900">{t('pageTitle')}</h1>
          </div>
          <div className="text-sm text-gray-500">
            {user?.firstName} {user?.lastName}
          </div>
        </div>
      </header>

      {/* Toolbar */}
      <div className="bg-white border-b border-gray-200 px-6 py-3">
        <div className="flex items-center justify-between">
          {/* Search */}
          <div className="relative w-80">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder={t('searchPlaceholder')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 input text-sm"
            />
          </div>

          {/* Actions */}
          <button
            onClick={handleCreate}
            className="flex items-center gap-2 px-4 py-2 btn-primary"
          >
            <Plus className="w-4 h-4" />
            {t('newCustomer')}
          </button>
        </div>
      </div>

      {/* Content */}
      <main className="flex-1 overflow-auto p-6">
        {isLoading ? (
          <div className="flex items-center justify-center h-64">
            <Loader2 className="w-8 h-8 text-emerald-600 animate-spin" />
          </div>
        ) : error ? (
          <div className="flex items-center justify-center h-64">
            <div className="text-center">
              <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
              <p className="text-gray-600">{t('loadError')}</p>
            </div>
          </div>
        ) : customers.length === 0 ? (
          <div className="flex items-center justify-center h-64">
            <EmptyState
              icon={Users}
              title={searchTerm ? t('noResults') : t('emptyTitle')}
              description={!searchTerm ? t('emptyHint') : undefined}
            />
          </div>
        ) : (
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
            {/* Table Header */}
            <div className="grid grid-cols-12 gap-4 px-6 py-3 bg-gray-50/50 border-b border-gray-100 text-xs font-medium text-gray-500 uppercase tracking-wider">
              <div className="col-span-4">{t('tableHeaders.customer')}</div>
              <div className="col-span-3">{t('tableHeaders.contacts')}</div>
              <div className="col-span-3">{t('tableHeaders.address')}</div>
              <div className="col-span-2 text-right">{t('tableHeaders.actions')}</div>
            </div>

            {/* Table Body */}
            <div className="divide-y divide-gray-100">
              {customers.map((customer) => (
                <div
                  key={customer.id}
                  role="button"
                  tabIndex={0}
                  onClick={() => setHistoryCustomer(customer)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      setHistoryCustomer(customer);
                    }
                  }}
                  title={t('history.openHint')}
                  className="grid grid-cols-12 gap-4 px-6 py-4 items-center cursor-pointer transition-colors hover:bg-gray-50"
                >
                  {/* Customer Info */}
                  <div className="col-span-4 flex items-center gap-3">
                    <CustomerAvatar customer={customer} />
                    <div className="min-w-0">
                      <p className="font-medium text-gray-900 truncate">
                        {customer.fullName}
                      </p>
                      <p className="text-sm text-gray-500">
                        {t('appointmentsCount', { count: customer.totalAppointments })}
                        {customer.upcomingAppointments > 0 && (
                          <span className="text-emerald-600 ml-2">
                            {t('upcomingCount', { count: customer.upcomingAppointments })}
                          </span>
                        )}
                        {customer.noShowCount > 0 && (
                          <span className="text-amber-600 ml-2">
                            ({customer.noShowCount} {t('noShow')})
                          </span>
                        )}
                      </p>
                      {customer.nextAppointmentAt ? (
                        <p className="mt-0.5 flex items-center gap-1.5 text-xs text-emerald-700">
                          <CalendarClock className="w-3 h-3 shrink-0" />
                          {t('nextOn', {
                            date: format(parseISO(customer.nextAppointmentAt), 'd MMM yyyy, HH:mm', {
                              locale: dateLocale,
                            }),
                          })}
                        </p>
                      ) : customer.lastAppointmentAt ? (
                        <p className="mt-0.5 flex items-center gap-1.5 text-xs text-gray-400">
                          <CalendarClock className="w-3 h-3 shrink-0" />
                          {t('lastOn', {
                            date: format(parseISO(customer.lastAppointmentAt), 'd MMM yyyy', {
                              locale: dateLocale,
                            }),
                          })}
                        </p>
                      ) : null}
                    </div>
                  </div>

                  {/* Contacts */}
                  <div className="col-span-3 space-y-1">
                    {customer.email && (
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <Mail className="w-4 h-4 text-gray-400" />
                        <span className="truncate">{customer.email}</span>
                      </div>
                    )}
                    {customer.phone && (
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <Phone className="w-4 h-4 text-gray-400" />
                        <span>{customer.phone}</span>
                      </div>
                    )}
                    {!customer.email && !customer.phone && (
                      <span className="text-sm text-gray-400 italic">{t('noContact')}</span>
                    )}
                  </div>

                  {/* Address */}
                  <div className="col-span-3">
                    {customer.address || customer.city ? (
                      <div className="flex items-start gap-2 text-sm text-gray-600">
                        <MapPin className="w-4 h-4 text-gray-400 mt-0.5 shrink-0" />
                        <span className="truncate">
                          {[customer.address, customer.city, customer.province]
                            .filter(Boolean)
                            .join(', ')}
                        </span>
                      </div>
                    ) : (
                      <span className="text-sm text-gray-400 italic">{t('noAddress')}</span>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="col-span-2 flex items-center justify-end gap-2">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleEdit(customer);
                      }}
                      className="p-2 text-gray-400 hover:text-emerald-600 hover:bg-emerald-50 rounded-lg transition-colors"
                      title={t('common:actions.edit')}
                    >
                      <Edit2 className="w-4 h-4" />
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDelete(customer);
                      }}
                      disabled={deleteCustomer.isPending}
                      className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50"
                      title={t('common:actions.delete')}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>

            {/* Footer */}
            <div className="px-6 py-3 bg-gray-50 border-t border-gray-200 text-sm text-gray-500">
              {customersResponse?.total ?? 0} {t('totalCustomers')}
            </div>
          </div>
        )}
      </main>

      {/* Modali */}
      <CustomerModal
        isOpen={isModalOpen}
        onClose={handleModalClose}
        customer={editingCustomer}
      />

      <CustomerAppointmentsModal
        customer={historyCustomer}
        onClose={() => setHistoryCustomer(null)}
        onSelectAppointment={(appointment) => handleSelectAppointment(appointment.id)}
      />
    </div>
  );
}

export default CustomersPage;
